import { TimelineElement } from 'ai-tutor-player';
import { CircleDot, Circle, RotateCw, Trash2 } from 'lucide-react';
import { useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';

import { useCanvasScale } from '@/lib/canvas';
import { useFreeformSnap, type GuideLine } from './FreeformSnapContext';
import { effectiveFreeformRect } from '@/lib/freeform';
import type { ScriptElement } from '@/lib/schema.types';
import { cn } from '@/lib/utils';
import { useEditorActions, useEditorStore } from '@/store/editor.store';

import { CalloutElement } from './elements/CalloutElement';
import { ChipElement } from './elements/ChipElement';
import { EmbedElement } from './elements/EmbedElement';
import { IconElement } from './elements/IconElement';
import { ImageElement } from './elements/ImageElement';
import { LatexElement } from './elements/LatexElement';
import { McqElement } from './elements/McqElement';
import { MermaidElement } from './elements/MermaidElement';
import { ShapeElement } from './elements/ShapeElement';
import { TextElement } from './elements/TextElement';
import { ThreeDElement } from './elements/ThreeDElement';
import { VideoElement } from './elements/VideoElement';
import { groupColor, useFreeformGroupMaybe } from './FreeformGroupContext';

type Handle = 'move' | 'nw' | 'ne' | 'sw' | 'se' | 'rotate';

interface SnapRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * Figma-style snapping while moving. Aligns the dragged rect's left/centre/right
 * (and top/middle/bottom) to sibling elements' edges/centres and the canvas
 * centre/edges, picking the nearest within `snap` px per axis. Returns the
 * adjusted x/y plus the guide lines to draw (spanning only between the aligned
 * boxes, like Figma).
 */
function computeSnap(
  rect: SnapRect,
  targets: SnapRect[],
  maxW: number,
  maxH: number,
  snap: number,
): { x: number; y: number; guides: GuideLine[] } {
  let { x, y } = rect;
  const { w, h } = rect;
  const guides: GuideLine[] = [];

  type Line = { pos: number; lo: number; hi: number };

  // ---- vertical lines (constant x) ----
  const vTargets: Line[] = [];
  for (const t of targets) {
    vTargets.push({ pos: t.x, lo: t.y, hi: t.y + t.h });
    vTargets.push({ pos: t.x + t.w / 2, lo: t.y, hi: t.y + t.h });
    vTargets.push({ pos: t.x + t.w, lo: t.y, hi: t.y + t.h });
  }
  vTargets.push({ pos: maxW / 2, lo: 0, hi: maxH });
  vTargets.push({ pos: 0, lo: 0, hi: maxH });
  vTargets.push({ pos: maxW, lo: 0, hi: maxH });

  const vEdges = [x, x + w / 2, x + w]; // left, centre, right
  let bestV: { d: number; t: Line } | null = null;
  for (const e of vEdges)
    for (const t of vTargets) {
      const d = t.pos - e;
      if (Math.abs(d) <= snap && (!bestV || Math.abs(d) < Math.abs(bestV.d))) bestV = { d, t };
    }
  if (bestV) {
    x += bestV.d;
    guides.push({
      axis: 'v',
      pos: bestV.t.pos,
      start: Math.min(y, bestV.t.lo),
      end: Math.max(y + h, bestV.t.hi),
    });
  }

  // ---- horizontal lines (constant y) ----
  const hTargets: Line[] = [];
  for (const t of targets) {
    hTargets.push({ pos: t.y, lo: t.x, hi: t.x + t.w });
    hTargets.push({ pos: t.y + t.h / 2, lo: t.x, hi: t.x + t.w });
    hTargets.push({ pos: t.y + t.h, lo: t.x, hi: t.x + t.w });
  }
  hTargets.push({ pos: maxH / 2, lo: 0, hi: maxW });
  hTargets.push({ pos: 0, lo: 0, hi: maxW });
  hTargets.push({ pos: maxH, lo: 0, hi: maxW });

  const hEdges = [y, y + h / 2, y + h]; // top, middle, bottom
  let bestH: { d: number; t: Line } | null = null;
  for (const e of hEdges)
    for (const t of hTargets) {
      const d = t.pos - e;
      if (Math.abs(d) <= snap && (!bestH || Math.abs(d) < Math.abs(bestH.d))) bestH = { d, t };
    }
  if (bestH) {
    y += bestH.d;
    guides.push({
      axis: 'h',
      pos: bestH.t.pos,
      start: Math.min(x, bestH.t.lo),
      end: Math.max(x + w, bestH.t.hi),
    });
  }

  return { x, y, guides };
}

interface Props {
  element: ScriptElement;
  slideId: string;
  rowId: string;
  columnId: string;
  /** Column index used to cascade un-positioned elements so they don't overlap. */
  stackIndex?: number;
}

export function FreeformElement({ element, slideId, rowId, columnId, stackIndex = 0 }: Props) {
  // The canvas may be rendered scaled (editor card / teach / preview). Pointer
  // deltas arrive in screen pixels; divide by the scale to get design-space px.
  const scale = useCanvasScale();
  const snap = useFreeformSnap();
  const isSelected = useEditorStore(
    (s) =>
      s.selection?.kind === 'element' &&
      s.selection.elementId === element.id &&
      s.selection.columnId === columnId,
  );
  const actions = useEditorActions();
  const group = useFreeformGroupMaybe();
  const inGroupSelection = group?.selected.has(element.id) ?? false;

  const basePos = effectiveFreeformRect(element.position, stackIndex);

  // Live drag state (refs to avoid stale closures, state to drive renders).
  type Pos = typeof basePos;
  const dragRef = useRef<{
    handle: Handle;
    startX: number;
    startY: number;
    startPos: Pos;
    confirmed: boolean;
    /** Element center in screen px — captured for rotation. */
    cx: number;
    cy: number;
    /** Sibling rects (design px) snapshotted at drag start, for snapping. */
    siblings: SnapRect[];
  } | null>(null);
  const livePosRef = useRef<Pos | null>(null);
  const [livePos, setLivePos] = useState<Pos | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const select = () =>
    useEditorStore
      .getState()
      .setSelection({ kind: 'element', slideId, rowId, columnId, elementId: element.id });

  const beginDrag = (handle: Handle) => (e: ReactPointerEvent<HTMLElement>) => {
    if (e.button !== 0) return;
    if (dragRef.current) return; // already tracking a drag
    const isMove = handle === 'move';

    // Resize handles always claim the pointer immediately. Move handles defer
    // preventDefault until the pointer actually moves so that single clicks
    // still reach interactive children (Tiptap caret, <model-viewer> camera,
    // <iframe>, <video>, etc.) for in-place editing/interaction.
    if (!isMove) {
      e.stopPropagation();
      e.preventDefault();
    }
    select();

    // Snapshot the freeform container bounds at drag start so we can clamp
    // the element inside it. The freeform row sets `position: relative` and is
    // the nearest positioned ancestor of our absolutely-positioned wrapper.
    const container =
      (wrapperRef.current?.offsetParent as HTMLElement | null) ?? null;
    const bounds = container
      ? { w: container.clientWidth, h: container.clientHeight }
      : null;

    // For rotation we need the element's on-screen center to measure the angle.
    const wrapRect = wrapperRef.current?.getBoundingClientRect();
    const cx = wrapRect ? wrapRect.left + wrapRect.width / 2 : e.clientX;
    const cy = wrapRect ? wrapRect.top + wrapRect.height / 2 : e.clientY;

    // Snapshot sibling rects (design px) once, for snap targets. Skip self and
    // same-group siblings (those travel together, so snapping to them is moot).
    const siblings: SnapRect[] = [];
    if (isMove) {
      const col = useEditorStore
        .getState()
        .script?.slides.find((sl) => sl.id === slideId)
        ?.rows.find((r) => r.id === rowId)
        ?.columns.find((c) => c.id === columnId);
      col?.elements.forEach((sib, j) => {
        if (sib.id === element.id) return;
        if (element.groupId && sib.groupId === element.groupId) return;
        const r = effectiveFreeformRect(sib.position, j);
        siblings.push({ x: r.x, y: r.y, w: r.w, h: r.h });
      });
    }

    dragRef.current = {
      handle,
      startX: e.clientX,
      startY: e.clientY,
      startPos: basePos,
      confirmed: !isMove,
      cx,
      cy,
      siblings,
    };

    const MIN_W = 40;
    const MIN_H = 20;

    const onMove = (ev: PointerEvent) => {
      const d = dragRef.current;
      if (!d) return;

      // Rotation is angle-based (not delta-based): measure the pointer angle
      // around the element center. Up = 0°. Shift snaps to 15° increments.
      if (d.handle === 'rotate') {
        ev.preventDefault();
        const angle = (Math.atan2(ev.clientY - d.cy, ev.clientX - d.cx) * 180) / Math.PI + 90;
        let rotation = Math.round(angle);
        if (ev.shiftKey) rotation = Math.round(rotation / 15) * 15;
        rotation = ((rotation % 360) + 360) % 360;
        const next = { ...d.startPos, rotation };
        livePosRef.current = next;
        setLivePos(next);
        return;
      }

      // Screen-space delta drives the gesture-confirm threshold (a fixed pixel
      // distance regardless of zoom); design-space delta drives positioning.
      const screenDx = ev.clientX - d.startX;
      const screenDy = ev.clientY - d.startY;
      if (!d.confirmed) {
        if (Math.hypot(screenDx, screenDy) < 4) return;
        d.confirmed = true;
      }
      const dx = screenDx / scale;
      const dy = screenDy / scale;
      // From the moment the drag is confirmed we own the pointer — block
      // text selection / native scroll / child interactions.
      ev.preventDefault();
      const s = d.startPos;
      const maxW = bounds?.w ?? Number.POSITIVE_INFINITY;
      const maxH = bounds?.h ?? Number.POSITIVE_INFINITY;
      let next: Pos = { ...s };
      switch (d.handle) {
        case 'move': {
          // Move: keep current size, slide x/y but clamp so the element stays
          // fully inside the freeform container.
          let x = Math.min(Math.max(0, s.x + dx), Math.max(0, maxW - s.w));
          let y = Math.min(Math.max(0, s.y + dy), Math.max(0, maxH - s.h));
          // Snap edges/centres to siblings + canvas centre/edges (hold Alt to
          // bypass). Guide lines are drawn by the surface via the snap context.
          if (!ev.altKey && Number.isFinite(maxW) && Number.isFinite(maxH)) {
            const SNAP = 6 / scale; // ~6 screen px regardless of zoom
            const res = computeSnap({ x, y, w: s.w, h: s.h }, d.siblings, maxW, maxH, SNAP);
            x = Math.min(Math.max(0, res.x), Math.max(0, maxW - s.w));
            y = Math.min(Math.max(0, res.y), Math.max(0, maxH - s.h));
            snap?.setGuides(res.guides);
          } else {
            snap?.setGuides([]);
          }
          next = { ...s, x, y };
          break;
        }
        case 'se': {
          const w = Math.min(Math.max(MIN_W, s.w + dx), Math.max(MIN_W, maxW - s.x));
          const h = Math.min(Math.max(MIN_H, s.h + dy), Math.max(MIN_H, maxH - s.y));
          next = { ...s, w, h };
          break;
        }
        case 'sw': {
          const right = s.x + s.w;
          const x = Math.min(Math.max(0, s.x + dx), right - MIN_W);
          const w = right - x;
          const h = Math.min(Math.max(MIN_H, s.h + dy), Math.max(MIN_H, maxH - s.y));
          next = { ...s, x, w, h };
          break;
        }
        case 'ne': {
          const bottom = s.y + s.h;
          const y = Math.min(Math.max(0, s.y + dy), bottom - MIN_H);
          const h = bottom - y;
          const w = Math.min(Math.max(MIN_W, s.w + dx), Math.max(MIN_W, maxW - s.x));
          next = { ...s, y, w, h };
          break;
        }
        case 'nw': {
          const right = s.x + s.w;
          const bottom = s.y + s.h;
          const x = Math.min(Math.max(0, s.x + dx), right - MIN_W);
          const y = Math.min(Math.max(0, s.y + dy), bottom - MIN_H);
          next = { ...s, x, y, w: right - x, h: bottom - y };
          break;
        }
      }
      livePosRef.current = next;
      setLivePos(next);
    };

    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      snap?.setGuides([]);
      const d = dragRef.current;
      const final = livePosRef.current;
      const wasDrag = !!d?.confirmed;
      dragRef.current = null;
      livePosRef.current = null;
      setLivePos(null);
      if (!wasDrag || !final) return;
      if (
        final.x === basePos.x &&
        final.y === basePos.y &&
        final.w === basePos.w &&
        final.h === basePos.h &&
        final.rotation === basePos.rotation
      ) {
        return;
      }
      actions.updateElement(slideId, rowId, columnId, element.id, {
        position: {
          x: final.x,
          y: final.y,
          w: final.w,
          h: final.h,
          rotation: final.rotation,
        },
      });

      // Group move: if this element is grouped and the gesture was a move (size
      // unchanged), shift every same-group sibling in this column by the same
      // delta so the whole group travels together.
      if (element.groupId && d?.handle === 'move') {
        const dx = final.x - basePos.x;
        const dy = final.y - basePos.y;
        if (dx !== 0 || dy !== 0) {
          const sc = useEditorStore.getState().script;
          const col = sc?.slides
            .find((s) => s.id === slideId)
            ?.rows.find((r) => r.id === rowId)
            ?.columns.find((c) => c.id === columnId);
          col?.elements.forEach((sib) => {
            if (sib.id === element.id || sib.groupId !== element.groupId) return;
            const sp = sib.position ?? {};
            actions.updateElement(slideId, rowId, columnId, sib.id, {
              position: { x: Math.max(0, (sp.x ?? 0) + dx), y: Math.max(0, (sp.y ?? 0) + dy) },
            });
          });
        }
      }
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  };

  const renderPos = livePos ?? basePos;

  return (
    <div
      ref={wrapperRef}
      style={{
        position: 'absolute',
        left: renderPos.x,
        top: renderPos.y,
        width: renderPos.w,
        height: renderPos.h,
        transform: renderPos.rotation ? `rotate(${renderPos.rotation}deg)` : undefined,
        zIndex: basePos.zIndex,
      }}
      onClick={(e) => {
        e.stopPropagation();
        select();
      }}
      className={cn(
        'group/free flex select-none flex-col rounded-md border border-transparent bg-transparent transition',
        isSelected
          ? 'cursor-move border-accent ring-1 ring-accent/40'
          : 'cursor-move hover:border-accent/40 hover:ring-1 hover:ring-accent/20',
      )}
    >
      {/* The body uses pointer-down capture so this wrapper sees the gesture
          BEFORE any child that calls e.stopPropagation() in bubble (e.g.
          ThreeDElement, iframes, video controls). preventDefault is deferred
          inside beginDrag until movement is confirmed, so plain clicks still
          fall through to interactive content. */}
      <div
        className={cn(
          'relative flex h-full w-full',
          // Images carry their own drop-shadow (and round to the picture, not the
          // box) — clipping here would chop the shadow off. Callouts likewise:
          // they rotate/swing and can carry a drop shadow, so a hard clip cuts
          // their corners off. Everything else clips.
          element.type === 'image' || element.type === 'callout'
            ? 'overflow-visible'
            : 'overflow-hidden',
        )}
        onPointerDownCapture={beginDrag('move')}
      >
        <Body
          element={element}
          slideId={slideId}
          rowId={rowId}
          columnId={columnId}
          isSelected={isSelected}
        />
        {/* While a drag is live, an invisible shield sits above interactive
            children to make sure pointermove/up don't get hijacked by e.g.
            <model-viewer>'s camera controls or a video's seek bar. */}
        {livePos && (
          <div className="absolute inset-0 z-20 cursor-move" aria-hidden />
        )}
      </div>

      {group && (
        <button
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            group.toggle(element.id);
          }}
          title={inGroupSelection ? 'Remove from group selection' : 'Select for grouping'}
          className={cn(
            'absolute left-1 top-1 z-30 grid h-5 w-5 place-items-center rounded-md border bg-white shadow-pill transition',
            inGroupSelection
              ? 'border-accent text-accent'
              : 'border-border text-fg-subtle opacity-0 group-hover/free:opacity-100',
          )}
        >
          {inGroupSelection ? <CircleDot size={12} /> : <Circle size={12} />}
        </button>
      )}
      {element.groupId && (
        <span
          className="absolute bottom-1 left-1 z-20 h-2.5 w-2.5 rounded-full ring-2 ring-white"
          style={{ background: groupColor(element.groupId) }}
          title="Grouped — drag any member to move the group"
        />
      )}

      {isSelected && (
        <>
          {(['nw', 'ne', 'sw', 'se'] as const).map((h) => (
            <span
              key={h}
              onPointerDown={beginDrag(h)}
              className={cn(
                'absolute z-10 h-2.5 w-2.5 rounded-sm border border-accent bg-white shadow-pill',
                h === 'nw' && '-left-1.5 -top-1.5 cursor-nwse-resize',
                h === 'ne' && '-right-1.5 -top-1.5 cursor-nesw-resize',
                h === 'sw' && '-bottom-1.5 -left-1.5 cursor-nesw-resize',
                h === 'se' && '-bottom-1.5 -right-1.5 cursor-nwse-resize',
              )}
              aria-label={`Resize ${h}`}
            />
          ))}
          {/* Rotation handle — sits above the top edge with a short connector. */}
          <span
            className="absolute left-1/2 top-0 z-10 h-4 w-px -translate-x-1/2 -translate-y-full bg-accent/60"
            aria-hidden
          />
          <button
            onPointerDown={beginDrag('rotate')}
            onClick={(e) => e.stopPropagation()}
            className="absolute left-1/2 top-0 z-20 grid h-5 w-5 -translate-x-1/2 -translate-y-[calc(100%+12px)] cursor-grab place-items-center rounded-full border border-accent bg-white text-accent shadow-pill active:cursor-grabbing"
            title="Drag to rotate (hold Shift to snap to 15°)"
            aria-label="Rotate element"
          >
            <RotateCw size={11} />
          </button>
          {livePos && dragRef.current?.handle === 'rotate' && (
            <span className="absolute left-1/2 top-1 z-30 -translate-x-1/2 rounded bg-fg/80 px-1.5 py-0.5 text-[10px] font-medium text-white">
              {Math.round(renderPos.rotation)}°
            </span>
          )}
          <button
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              actions.removeElement(slideId, rowId, columnId, element.id);
            }}
            className="absolute right-1 top-1 z-30 grid h-6 w-6 place-items-center rounded-md border border-border bg-white text-fg-subtle shadow-pill hover:text-red-500"
            title="Delete element"
          >
            <Trash2 size={11} />
          </button>
        </>
      )}
    </div>
  );
}

function Body({
  element,
  slideId,
  rowId,
  columnId,
  isSelected,
}: Props & { isSelected: boolean }) {
  switch (element.type) {
    case 'text':
      return (
        <TextElement
          element={element}
          slideId={slideId}
          rowId={rowId}
          columnId={columnId}
          isSelected={isSelected}
        />
      );
    case 'image':
      return <ImageElement element={element} fill />;
    case 'shape':
      return <ShapeElement element={element} fillBox />;
    case 'mcq':
      return <McqElement element={element} />;
    case 'chip':
      return <ChipElement element={element} />;
    case 'video':
      return <VideoElement element={element} />;
    case 'embed':
      return <EmbedElement element={element} isSelected={isSelected} />;
    case '3d':
      return <ThreeDElement element={element} fill />;
    case 'latex':
      return <LatexElement element={element} fill />;
    case 'icon':
      return <IconElement element={element} />;
    case 'callout':
      return <CalloutElement element={element} />;
    case 'mermaid':
      return <MermaidElement element={element} fill />;
    case 'timeline':
      return <TimelineElement element={element} fill />;
    default:
      return null;
  }
}
